import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Zap } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) navigate("/dashboard");
    else toast.error(r.error || "Login failed");
  }

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-1/2 relative items-end p-12 grid-lines"
           style={{
             backgroundImage:
               "linear-gradient(135deg, rgba(0,122,255,0.15), transparent 60%), url(https://images.pexels.com/photos/29506609/pexels-photo-29506609.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
             backgroundSize: "cover", backgroundBlendMode: "overlay", backgroundColor: "#050505"
           }}>
        <div>
          <h1 className="text-5xl font-[Chivo] tracking-tight leading-none">
            Test<br/>strategies at<br/>the speed of <span className="text-[#00E676]">light</span>.
          </h1>
          <p className="mt-6 text-neutral-400 max-w-md">
            An educational micro-trading sandbox for stocks &amp; crypto. Build, simulate, and learn — scalping, arbitrage,
            market-making and indicator strategies. Not investment advice.
          </p>
        </div>
      </div>

      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-8 h-8 bg-[#007AFF] flex items-center justify-center"><Zap size={18} className="text-white" /></div>
            <div>
              <div className="font-[Chivo] tracking-tight">MICRO/TRADE</div>
              <div className="label-xs">Execution Engine</div>
            </div>
          </div>

          <h2 className="text-3xl font-[Chivo] tracking-tight mb-1">Sign in</h2>
          <p className="text-sm text-neutral-400 mb-8">Access your trading dashboard.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="label-xs block mb-2">Email</label>
              <Input
                data-testid="login-email"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="bg-[#0F0F0F] border-border focus:ring-1 focus:ring-[#007AFF]" autoComplete="email"
              />
            </div>
            <div>
              <label className="label-xs block mb-2">Password</label>
              <Input
                data-testid="login-password"
                type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                className="bg-[#0F0F0F] border-border focus:ring-1 focus:ring-[#007AFF]" autoComplete="current-password"
              />
            </div>
            <Button
              data-testid="login-submit"
              type="submit" disabled={loading}
              className="w-full bg-[#007AFF] hover:bg-[#3395FF] text-white font-[Chivo] tracking-wide"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-xs text-neutral-500" data-testid="personal-use-note">
            Personal-use private trading bot · Admin-managed accounts only.
          </div>
        </div>
      </div>
    </div>
  );
}
